import { t } from '@/features/i18n/utils';
import { createFileRoute, notFound, redirect } from '@tanstack/react-router';
import { useSuspenseQuery } from '@tanstack/react-query';
import { BidBreadcrumbs } from '@/features/bids/components/bid-breadcrumbs';
import { BidDeliverablesTable } from '@/features/bid-deliverables/components/bid-deliverables-table';
import { BidDeliverablesBoard } from '@/features/bid-deliverables/components/bid-deliverables-board';
import { CriterionInfo } from '@/features/bid-deliverables/components/bid-deliverables';
import {
  BID_COMPLIANCE_CRITERION_ID,
  BID_QUALIFICATION_CRITERION_ID,
} from '@/features/bid-deliverables/constants';
import {
  deliverablesViewAtom,
  pendingDeliverableFilesAtom,
} from '@/features/bid-deliverables/store';
import { BidDeliverablesFilterMenu } from '@/features/bid-deliverables/components/bid-deliverables-filter-menu';
import { BidDeliverablesDisplayMenu } from '@/features/bid-deliverables/components/bid-deliverables-display-menu';
import { DeliverableUploadsActionBar } from '@/features/bid-deliverables/components/bid-deliverable-uploads';
import { BidDeliverablesRefreshAlert } from '@/features/bid-deliverables/components/bid-deliverables-refresh-alert';
import { bidComplianceQuery } from '@/features/bid-compliance/queries';
import { weightPercent } from '@/features/bids/utils';
import { Badge } from '@/ui/components/badge';
import { BreadcrumbLink, BreadcrumbPage } from '@/ui/components/breadcrumb';
import { Header, HeaderBackButton, HeaderChatButton } from '@/ui/components/header';
import { H1 } from '@/ui/components/heading';
import {
  MainActions,
  MainActionsEnd,
  MainHeader,
  MainHeaderBackButton,
  MainScrollArea,
} from '@/ui/components/main';
import { getDefaultStore, useAtomValue } from 'jotai';

export const Route = createFileRoute(
  '/_authed/project/$projectId/workspaces/$workspaceId/bids/$bidId/_layout/deliverables/$criterionId/',
)({
  beforeLoad: ({ params }) => {
    if (params.criterionId !== BID_QUALIFICATION_CRITERION_ID) return;
    throw redirect({
      to: '/project/$projectId/workspaces/$workspaceId/bids/$bidId/deliverables',
      params,
      search: { section: 'qualification' },
    });
  },
  loader: async ({ context, params }) => {
    const compliance = await context.queryClient.query({
      ...bidComplianceQuery(params.workspaceId, params.bidId),
      staleTime: 'static',
    });
    if (params.criterionId === BID_COMPLIANCE_CRITERION_ID) return;
    if (
      !compliance.deliverableCatalog?.tenderCriteria.some(
        (criterion) => criterion.id === params.criterionId,
      )
    )
      throw notFound();
  },
  onEnter: () => getDefaultStore().set(pendingDeliverableFilesAtom, []),
  component: RouteComponent,
});

function RouteComponent() {
  const view = useAtomValue(deliverablesViewAtom);
  const { projectId, workspaceId, bidId, criterionId } = Route.useParams();
  const { data: compliance } = useSuspenseQuery(bidComplianceQuery(workspaceId, bidId));
  const criterion = compliance.deliverableCatalog?.tenderCriteria.find(
    (item) => item.id === criterionId,
  );
  const title = criterion?.name ?? t({ en: 'Compliance', nb: 'Samsvar' });

  return (
    <>
      <Header>
        <HeaderBackButton
          to="/project/$projectId/workspaces/$workspaceId/bids/$bidId/deliverables"
          params={{ projectId, workspaceId, bidId }}
          search={{ section: 'tender' }}
        />
        <BidBreadcrumbs>
          <BreadcrumbLink
            to="/project/$projectId/workspaces/$workspaceId/bids/$bidId/deliverables"
            params={{ projectId, workspaceId, bidId }}
            search={{ section: 'tender' }}
          >
            {t({ en: 'Tender', nb: 'Tilbud' })}
          </BreadcrumbLink>
          <BreadcrumbPage>{title}</BreadcrumbPage>
        </BidBreadcrumbs>
        <HeaderChatButton />
      </Header>
      <MainScrollArea>
        <BidDeliverablesRefreshAlert />
        <MainHeader>
          <H1>
            <MainHeaderBackButton
              to="/project/$projectId/workspaces/$workspaceId/bids/$bidId/deliverables"
              params={{ projectId, workspaceId, bidId }}
              search={{ section: 'tender' }}
            />
            <span className="line-clamp-1">{title}</span>
            {criterion ? (
              <Badge size="sm" kind="with-icon" className="ml-2 self-center shrink-0">
                {criterion.weight != null ? (
                  <span>
                    {weightPercent(criterion.weight)} %{' '}
                    <span className="text-muted font-normal">
                      {t({ en: 'of bid', nb: 'av tilbudet' })}
                    </span>
                  </span>
                ) : null}
                <CriterionInfo criterion={criterion} className="text-muted" />
              </Badge>
            ) : null}
          </H1>
        </MainHeader>
        <MainActions>
          <BidDeliverablesFilterMenu />
          <MainActionsEnd>
            <BidDeliverablesDisplayMenu />
          </MainActionsEnd>
        </MainActions>
        {view === 'list' ? (
          <BidDeliverablesTable criterionId={criterionId} />
        ) : (
          <BidDeliverablesBoard criterionId={criterionId} />
        )}
      </MainScrollArea>
      <DeliverableUploadsActionBar />
    </>
  );
}
